import { isNonEmptyArray, isPresent } from "lib/utils";
import { useMemo } from "react";
import { useSelector } from "react-redux";
import { selectCities } from "store/api";
import { selectCity } from "store/auth";

// Бегущая строка в шапке. Источник — site_alerts с type='ticker'
// (едут в общем ответе /v1/cities/indexnew внутри cities[].alerts —
// отдельных запросов нет). pages у тикеров игнорируем: записи публикуются
// с pages=['_ticker'], чтобы старый рендер плашек (useAlerts) их не подхватывал.
const BAR =
  "bg-logo hidden lg:block py-2 text-sm text-white leading-6 tracking-tight min-h-[40px]";

const useTickerItems = (): string[] => {
  // ВАЖНО: язык жёстко "ru" и город = выбранный ИЛИ первый — ровно как в Header.
  // Через useSlug().queryLng селектор отдавал пустой массив, а selectCity на
  // первом заходе пуст → find не находил город и строка была пустой (05.08).
  const city = useSelector(selectCity);
  const cities = useSelector(selectCities("ru"));

  return useMemo(() => {
    const cityInfo =
      cities.find((c: any) => c.code === city) ??
      (isNonEmptyArray(cities) ? cities[0] : null);
    const alerts: any[] = isPresent(cityInfo) ? (cityInfo as any).alerts : [];
    return alerts
      .filter((a) => a?.type === "ticker")
      .map((a) => String(a?.title ?? a?.text ?? ""))
      .map((s) => s.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim())
      .filter(Boolean);
  }, [cities, city]);
};

// styled-jsx в проекте не используется (ESLint: react/no-unknown-property на jsx),
// поэтому обычный <style>. Классы mf-ticker__* уникальны — глобальность безопасна.
const TICKER_CSS = `
.mf-ticker__track {
  display: flex;
  width: max-content;
  will-change: transform;
  animation-name: mf-ticker-scroll;
  animation-timing-function: linear;
  animation-iteration-count: infinite;
}
.mf-ticker__seq {
  white-space: nowrap;
  display: inline-flex;
}
/* Расстояние МЕЖДУ фразами и между последней фразой и началом повтора. */
.mf-ticker__item {
  white-space: nowrap;
  margin-right: 3rem;
}
.mf-ticker:hover .mf-ticker__track {
  animation-play-state: paused;
}
@keyframes mf-ticker-scroll {
  from { transform: translate3d(0, 0, 0); }
  to { transform: translate3d(-50%, 0, 0); }
}
@media (prefers-reduced-motion: reduce) {
  .mf-ticker__track { animation: none; }
  .mf-ticker__seq + .mf-ticker__seq { display: none; }
}
`;

type Props = { fallback?: string };

// CLS-паттерн тот же, что был у headerMsg-полосы: блок рендерится ВСЕГДА,
// высота 40px (py-2 8+8 + leading-6 24) совпадает во всех состояниях → shift = 0.
// Едет только transform → GPU-композитинг, layout не пересчитывается.
const Ticker = ({ fallback }: Props) => {
  const items = useTickerItems();
  // Разделителя между фразами нет — расстояние задаётся margin у .mf-ticker__item.
  const line = items.join("   ");

  if (!line)
    return <div className={`${BAR} text-center`}>{fallback || " "}</div>;

  // скорость ~ длине контента: 3 фразы и 6 фраз читаются одинаково
  const duration = Math.min(90, Math.max(20, Math.round(line.length * 0.32)));

  return (
    <div className={`mf-ticker ${BAR} overflow-hidden`}>
      <div
        className="mf-ticker__track"
        style={{ animationDuration: `${duration}s` }}
      >
        <span className="mf-ticker__seq">
          {items.map((t, i) => (
            <span className="mf-ticker__item" key={`a${i}`}>
              {t}
            </span>
          ))}
        </span>
        <span className="mf-ticker__seq" aria-hidden="true">
          {items.map((t, i) => (
            <span className="mf-ticker__item" key={`b${i}`}>
              {t}
            </span>
          ))}
        </span>
      </div>
      <style dangerouslySetInnerHTML={{ __html: TICKER_CSS }} />
    </div>
  );
};

export default Ticker;
